import { useCompanyId } from "@context-providers/company-id-provider";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { RoleDisplayNames } from "@custom-types/member-types";
import { SdbProject } from "@custom-types/project-types";
import { neutral, TeamIcon, UserCardItem, useToast } from "@flat-ui";
import { useState } from "react";
import { useChangeMemberRole } from "@hooks/use-change-member-role";
import { useMembersUtils } from "@hooks/use-member-utils";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { FaroCoreApiConst, FaroCoreApiTypes } from "@stellar/core-api-client";
import { formatUserRoleType } from "@utils/data-display";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { isTeam } from "@utils/team-utils";
import { getPendingUserIcon, isMemberActive } from "@utils/user-utils";
import { isProjectRole } from "@custom-types/member-type-guards";

export interface SelectedCardItemOption {
  /** The target element of the selected option */
  target: { value: string };
}

interface Props extends BaseProjectIdProps {
  /** The project member to render */
  member: FaroCoreApiTypes.ProjectMemberBase;

  /** Whether the remove option should be hidden for this member */
  shouldHideRemove: boolean;

  /** Whether the current user is allowed to change this member's role */
  isRoleEditable: boolean;

  /** Callback function when an option is selected from the dropdown */
  onSelectOption(
    option: SelectedCardItemOption,
    member: FaroCoreApiTypes.ProjectMemberBase
  ): void;

  /** Location from which the member list is rendered, for tracking purposes. */
  location: "share-project-dialog";
}

/** Renders a project member using the UserCardItem component */
export function ProjectMember({
  member,
  shouldHideRemove,
  isRoleEditable,
  onSelectOption,
  projectId,
  location,
}: Props): JSX.Element {
  const { openToast } = useToast();
  const { openProfilePage, openViewTeamMembers } = useMembersUtils();
  const companyId = useCompanyId();
  const { changeMemberRole } = useChangeMemberRole({
    companyId,
    location,
    subjectType: "project",
    memberIdentity: member.identity,
    currentRole: member.role,
    selectedProject: { id: projectId } as SdbProject,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string>(member.role);

  /** Is the member a team */
  const isMemberTeam = isTeam(member);

  const roleDisplayName = RoleDisplayNames[member.role];

  /** The owner role cannot be reassigned to or away from */
  const isOwner = member.role === "RESOURCE_OWNER";

  // When the role can be edited, offer all project roles (owner disabled), else
  // show only the current role as a read-only entry.
  const roleDropdownOptions =
    isRoleEditable && !isOwner
      ? FaroCoreApiConst.allProjectRoles.map((role) => ({
          key: role,
          label: formatUserRoleType(role),
          value: role,
          isDisabled: role === "RESOURCE_OWNER",
        }))
      : [
          {
            key: member.role,
            label: formatUserRoleType(member.role),
            value: member.role,
          },
        ];

  /**
   * Handles an option selected from the card dropdown: either forwards the
   * removal request or applies the selected role change.
   */
  async function onOptionSelected({
    target,
  }: SelectedCardItemOption): Promise<void> {
    // Prevent duplicate submissions while already loading
    if (isLoading) {
      return;
    }

    if (target.value === "remove") {
      onSelectOption({ target }, member);
      return;
    }

    if (!isProjectRole(target.value)) {
      return;
    }

    try {
      setIsLoading(true);

      // Update the selected option immediately for a responsive UI as happy path,
      // but revert to the original role if the API call fails.
      setSelectedOption(target.value);

      await changeMemberRole(target.value);
    } catch {
      // Errors are handled by the respective slices

      // Revert to the original role if it fails to update the role
      setSelectedOption(member.role);
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Opens the profile page of the user or the team page members.
   * The permission validation is done in the canOpenProfilePage variable.
   */
  function openProfileOrTeamMembersClick(
    member: FaroCoreApiTypes.ProjectMemberBase
  ): void {
    if (!isMemberTeam) {
      openProfilePage({
        member,
        source: "user more info menu project member list",
      });
      return;
    }

    if (member.id) {
      openViewTeamMembers({ teamId: member.id });
      return;
    }

    openToast({
      title: `${capitalizeFirstLetter(
        TEAM_DISPLAY_NAME
      )} profile page cannot be opened`,
      message: `The ${TEAM_DISPLAY_NAME} ID is not available. Please reload the page to try again.`,
      variant: "error",
    });
  }

  return (
    <UserCardItem
      key={member.identity}
      shouldShowProfileCard={true}
      actionButtonLabel={isMemberTeam ? "View Members" : "Profile"}
      onProfileCardClick={() => openProfileOrTeamMembersClick(member)}
      shouldHideRemove={shouldHideRemove}
      icon={isMemberTeam ? <TeamIcon /> : getPendingUserIcon(member)}
      displayOption={isMemberActive(member) ? "nameOnly" : "emailOnly"}
      avatarSx={isMemberTeam ? { bgcolor: neutral[700] } : undefined}
      isLoading={isLoading}
      isDisabled={false}
      userInfo={{
        name: member.name,
        identity: member.identity,
        email: isMemberTeam ? "" : member.email,
        profileImageUrl: member.thumbnailUrl,
        userRole: roleDisplayName,
      }}
      selectedOption={selectedOption}
      dropdownOptions={roleDropdownOptions}
      onChange={(option) => void onOptionSelected(option)}
    />
  );
}
